import Bugsnag from '@bugsnag/js';
import { Observable, throwError } from 'rxjs';
import { setAttributes } from './element.utils';
import { validString } from './types.utils';

/**
 * Timeout in milliseconds
 */
export const SCRIPT_TIMEOUT = 5000;

export interface AddScriptOptions {
  [k: string]: any;
  subresourceIntegrity?: string;
  onLoad?: Function;
}

/**
 * Adds a script to the <head> section
 * @param url The url to load
 * @param async If you want it to be async or not
 * @param options The script options
 */
export function addScript(url, async = true, options: AddScriptOptions = {}): Observable<string> {
  if (!validString(url)) {
    return throwError(() => new Error('invalid script url'));
  }

  return new Observable((subscriber) => {
    const { id } = options;

    if (validString(id) && document.getElementById(id)) {
      subscriber.next(id);
      subscriber.complete();
      return;
    }

    const scriptEl: HTMLScriptElement = document.createElement('script');
    setAttributes(scriptEl, options);

    if (async) {
      scriptEl.async = true;
    }

    if (id) {
      scriptEl.id = id;
    }

    if (validString(options.subresourceIntegrity)) {
      scriptEl.integrity = options.subresourceIntegrity;
    }

    if (validString(options.crossOrigin)) {
      scriptEl.crossOrigin = options.crossOrigin;
    }

    scriptEl.onload = () => {
      subscriber.next(id);
      subscriber.complete();
    };
    scriptEl.onerror = (error) => {
      const errorEvent = new Error('Failed to load resource');
      Bugsnag.notify(errorEvent, (event) => {
        event.addMetadata('Resource', {
          error,
          file: url,
          options,
        });
      });
      subscriber.error(errorEvent);
      subscriber.complete();
    };
    scriptEl.onabort = (error) => {
      const errorEvent = new Error('Resource aborted');
      Bugsnag.notify(errorEvent, (event) => {
        event.addMetadata('Resource', {
          error,
          file: url,
          options,
        });
      });
      subscriber.error(errorEvent);
      subscriber.complete();
    };

    scriptEl.src = url;

    document.head.appendChild(scriptEl);
  });
}
